import { Grid, Icon, useAlert } from "@cd2/components"
import React from "react"
import { IRecordType } from "../types/schema"
import { formattedRecordName } from "../constants/humanisedRecordNames"

export function BasicAlertWithIcon({
  icon,
  children,
}: {
  icon: string
  children: React.ReactNode
}) {
  return (
    <Grid columns={["min-content", "auto"]} gap={16} alignItems={"center"}>
      <Icon icon={icon} size={14} />
      <div>{children}</div>
    </Grid>
  )
}

export function useSiteAlerts() {
  const { addAlert } = useAlert()

  function deleteAlert(recordType: IRecordType) {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="trash-alt">
          {formattedRecordName(recordType)} has been deleted
        </BasicAlertWithIcon>
      ),
      status: "ok",
      position: "top",
    })
  }

  function createAlert(recordType: IRecordType) {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">
          {formattedRecordName(recordType)} has been created
        </BasicAlertWithIcon>
      ),
      status: "ok",
      position: "top",
    })
  }

  function updateAlert(recordType: IRecordType) {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">
          {formattedRecordName(recordType)} has been updated
        </BasicAlertWithIcon>
      ),
      status: "ok",
      position: "top",
    })
  }

  function genericSuccess(message: string) {
    addAlert({
      content: <BasicAlertWithIcon icon="highlighter">{message}</BasicAlertWithIcon>,
      status: "ok",
      position: "top",
    })
  }

  function orderUpdatedAlert() {
    addAlert({
      content: <BasicAlertWithIcon icon="highlighter">Order has been updated</BasicAlertWithIcon>,
      status: "ok",
      position: "top",
    })
  }

  function bulkCreateAlert(recordType: IRecordType) {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">
          {formattedRecordName(recordType)}s have been created
        </BasicAlertWithIcon>
      ),
      status: "ok",
      position: "top",
    })
  }
  function bulkUpdateAlert(recordType: IRecordType) {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">
          {formattedRecordName(recordType)}s have been updated
        </BasicAlertWithIcon>
      ),
      status: "ok",
      position: "top",
    })
  }

  function removeAlert(recordType: IRecordType) {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">
          {formattedRecordName(recordType)} has been removed
        </BasicAlertWithIcon>
      ),
      status: "ok",
      position: "top",
    })
  }

  function addRecordAlert(recordType: IRecordType) {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">
          {formattedRecordName(recordType)} has been added
        </BasicAlertWithIcon>
      ),
      status: "ok",
      position: "top",
    })
  }

  function errorUpdatingAlert(recordType: IRecordType) {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="exclamation-triangle">
          There has been an issue updating this {recordType}. Please try again.
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function errorCreatingAlert(recordType: IRecordType) {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="exclamation-triangle">
          There has been an issue creating this {recordType}. Please try again.
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function errorDuplicatingAlert(recordType: IRecordType) {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="exclamation-triangle">
          There has been an issue duplicating this {recordType}. Please try again.
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function errorDeletingAlert(recordType: IRecordType) {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="exclamation-triangle">
          There has been an issue deleting this {recordType}. Please try again.
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function genericError(message = "An error has occurred. Please try again.") {
    addAlert({
      content: <BasicAlertWithIcon icon="exclamation-triangle">{message}</BasicAlertWithIcon>,
      status: "error",
      position: "top",
    })
  }

  function emailInUse() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="exclamation-triangle">
          Email address is already in use
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function tooManyUsers() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="exclamation-triangle">
          You already have the maximum number of users allowed.
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function passwordResetSentAlert() {
    addAlert({
      content: <BasicAlertWithIcon icon="envelope">Password reset email sent.</BasicAlertWithIcon>,
      status: "ok",
      position: "top",
    })
  }

  function inviteResentAlert() {
    addAlert({
      content: <BasicAlertWithIcon icon="envelope">Invitation has been resent.</BasicAlertWithIcon>,
      status: "ok",
      position: "top",
    })
  }

  function errorRemovingAlert(recordType: IRecordType) {
    addAlert({
      content: <BasicAlertWithIcon icon="times">Removing {recordType} failed.</BasicAlertWithIcon>,
      status: "error",
      position: "top",
    })
  }

  function emailDoesNotExistForReset() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="times">
          That email does not seem to exist, please check and try again
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function signInInformationIncorrect() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="times">
          That email or password appears to be incorrect, please check and try again
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function payrollSignInInformationIncorrect() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="times">
          That company prefix or unique identifier or password appears to be incorrect, please check
          and try again
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function permissionDenied() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="times">
          You do not have permission to perform this action.
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function termsAccepted() {
    addAlert({
      content: <BasicAlertWithIcon icon="highlighter">Terms accepted</BasicAlertWithIcon>,
      status: "ok",
      position: "top",
    })
  }

  function termsDeclined() {
    addAlert({
      content: <BasicAlertWithIcon icon="times">Terms declined</BasicAlertWithIcon>,
      status: "error",
      position: "top",
    })
  }

  function googlePopupClosed() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="times">
          You closed the Google authentication popup without signing in.
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function googleMustAcceptScopes() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="times">
          You didn{`'`}t give the platform permission to sync with your Google Calendar.
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function microsoftPopupClosed() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="times">
          You closed the Outlook authentication popup without signing in.
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function microsoftPopupAlreadyOpen() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="times">
          You already have an Outlook authentication popup open.
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function microsoftMustAcceptScopes() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="times">
          You didn{`'`}t give the platform permission to sync with your Outlook Calendar.
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function restoreAlert(recordType: string) {
    addAlert({
      content: <BasicAlertWithIcon icon="highlighter">{recordType} restored</BasicAlertWithIcon>,
      status: "ok",
    })
  }

  function invalidToken(type: "Invite" | "Reset") {
    addAlert({
      content: <BasicAlertWithIcon icon="times">{type} token is in invalid</BasicAlertWithIcon>,
      status: "error",
      position: "top",
    })
  }

  function flowHasArchivedSteps() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="times">
          Cannot submit - flow has archived steps
        </BasicAlertWithIcon>
      ),
      status: "error",
      position: "top",
    })
  }

  function pdfQueued() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">
          Pdf queued for processing, download will be available shortly.
        </BasicAlertWithIcon>
      ),
      status: "ok",
    })
  }

  function subscriptionChanged() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">
          Your subscription has been updated
        </BasicAlertWithIcon>
      ),
      status: "ok",
    })
  }

  function billingInfoChanged() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">
          Your billing info has been updated
        </BasicAlertWithIcon>
      ),
      status: "ok",
    })
  }

  function subscriptionCreated() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">
          Your subscription has been created
        </BasicAlertWithIcon>
      ),
      status: "ok",
    })
  }

  function subscriptionCancelled() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">
          Your subscription has been cancelled
        </BasicAlertWithIcon>
      ),
      status: "ok",
    })
  }

  function paymentCreated() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">Your payment has been made</BasicAlertWithIcon>
      ),
      status: "ok",
    })
  }

  function eventAdded() {
    addAlert({
      content: <BasicAlertWithIcon icon="highlighter">Event Added</BasicAlertWithIcon>,
      status: "ok",
    })
  }

  function fileAccessApproved() {
    addAlert({
      content: <BasicAlertWithIcon icon="highlighter">File approved</BasicAlertWithIcon>,
      status: "ok",
    })
  }

  function fileAccessDenied() {
    addAlert({
      content: <BasicAlertWithIcon icon="highlighter">File denied</BasicAlertWithIcon>,
      status: "ok",
    })
  }

  function otpSent() {
    addAlert({
      content: <BasicAlertWithIcon icon="highlighter">One-time password sent</BasicAlertWithIcon>,
      status: "ok",
    })
  }

  function otpCantVerify() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">
          This account requires Multi-factor authentication but does not have an email to send the
          code too
        </BasicAlertWithIcon>
      ),
      status: "error",
    })
  }

  function invalidOTP() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">One-time password invalid</BasicAlertWithIcon>
      ),
      status: "error",
    })
  }

  function otpTimeout() {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="highlighter">
          We{`'`}ve already sent you a one-time password recently.
        </BasicAlertWithIcon>
      ),
      status: "error",
    })
  }

  function draftCreated() {
    addAlert({
      content: <BasicAlertWithIcon icon="highlighter">Draft created</BasicAlertWithIcon>,
      status: "ok",
    })
  }

  function draftUpdated() {
    addAlert({
      content: <BasicAlertWithIcon icon="highlighter">Draft updated</BasicAlertWithIcon>,
      status: "ok",
    })
  }

  function draftPublished() {
    addAlert({
      content: <BasicAlertWithIcon icon="check">Draft published</BasicAlertWithIcon>,
      status: "ok",
    })
  }

  function draftScrapped() {
    addAlert({
      content: <BasicAlertWithIcon icon="trash-alt">Draft scrapped</BasicAlertWithIcon>,
      status: "ok",
    })
  }

  function recordArchived(recordType: IRecordType) {
    addAlert({
      content: (
        <BasicAlertWithIcon icon="trash-alt">
          {formattedRecordName(recordType)} archived
        </BasicAlertWithIcon>
      ),
      status: "ok",
      position: "top",
    })
  }

  return {
    deleteAlert,
    removeAlert,
    addRecordAlert,
    errorCreatingAlert,
    errorUpdatingAlert,
    errorDeletingAlert,
    updateAlert,
    bulkUpdateAlert,
    bulkCreateAlert,
    createAlert,
    passwordResetSentAlert,
    inviteResentAlert,
    errorRemovingAlert,
    emailDoesNotExistForReset,
    genericError,
    signInInformationIncorrect,
    payrollSignInInformationIncorrect,
    errorDuplicatingAlert,
    permissionDenied,
    termsAccepted,
    termsDeclined,
    googlePopupClosed,
    googleMustAcceptScopes,
    microsoftPopupClosed,
    microsoftPopupAlreadyOpen,
    microsoftMustAcceptScopes,
    restoreAlert,
    orderUpdatedAlert,
    invalidToken,
    flowHasArchivedSteps,
    emailInUse,
    pdfQueued,
    tooManyUsers,
    subscriptionChanged,
    billingInfoChanged,
    subscriptionCreated,
    subscriptionCancelled,
    eventAdded,
    fileAccessApproved,
    fileAccessDenied,
    draftCreated,
    draftUpdated,
    draftPublished,
    draftScrapped,
    recordArchived,
    paymentCreated,
    invalidOTP,
    otpTimeout,
    otpSent,
    otpCantVerify,
    genericSuccess,
  }
}
